import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Disc3, Music2, Radio, Newspaper, MapPin, BookOpen, MessagesSquare, User2, Sparkles, Hash, Gamepad2, ShoppingBag, Image as Picture } from "lucide-react";
import { ALBUMS, EPS, SONGS } from "@/data/eiffel65";
import { ALBUM_COVERS, EP_COVERS, resolveSongCover } from "@/data/covers";
import { scoreMatch, normalise, expandQuery } from "@/utils/searchUtils";
import Tx from "@/components/Tx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Spotlight-style global search overlay. Triggered via Navbar magnifier
 * or `Cmd/Ctrl + K`. Searches the static catalog client-side (tolerant
 * to typos + synonyms) and DB-backed content via `/api/search`.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <SearchOverlay open={open} onClose={() => setOpen(false)} />
 */
export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [remote, setRemote] = useState({});
  const [busy, setBusy] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // === Keyboard ===
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    setTimeout(() => inputRef.current?.focus(), 60);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  // === Debounced backend search ===
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setRemote({}); return; }
    let alive = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search`, { params: { q: term, limit: 6 } });
        if (alive) setRemote(data || {});
      } catch { if (alive) setRemote({}); }
      finally { if (alive) setBusy(false); }
    }, 220);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  // === Catalog search (client-side, tolerant) ===
  const catalogResults = useMemo(() => {
    const term = q.trim();
    if (term.length < 1) return { songs: [], albums: [], eps: [] };
    const expanded = expandQuery(term);
    const score = (text) => scoreMatch(text, expanded);

    const songs = SONGS
      .map((s) => ({ ...s, _score: Math.max(score(s.title), score(s.album || "")) }))
      .filter((s) => s._score > 0.15)
      .sort((a, b) => b._score - a._score)
      .slice(0, 8);

    const albums = ALBUMS
      .map((a) => ({ ...a, _score: Math.max(score(a.title), score(a.label || "")) }))
      .filter((a) => a._score > 0.15)
      .sort((a, b) => b._score - a._score)
      .slice(0, 4);

    const eps = EPS
      .map((e) => ({ ...e, _score: Math.max(score(e.title), score(e.label || "")) }))
      .filter((e) => e._score > 0.15)
      .sort((a, b) => b._score - a._score)
      .slice(0, 4);

    return { songs, albums, eps };
  }, [q]);

  // Flatten all results into an indexable list for keyboard arrows.
  const flat = useMemo(() => {
    const r = [];
    catalogResults.songs.forEach((s) => r.push({ kind: "song", id: s.id, label: s.title, sub: `Single · ${s.year}`, ref: s }));
    catalogResults.albums.forEach((a) => r.push({ kind: "album", id: a.id, label: a.title, sub: `Album · ${a.year}`, ref: a }));
    catalogResults.eps.forEach((e) => r.push({ kind: "ep", id: e.id, label: e.title, sub: `EP · ${e.year}`, ref: e }));
    (remote.users || []).forEach((u) => r.push({ kind: "user", id: u.username, label: `@${u.username}`, sub: u.bio || u.custom_title || "Fan", ref: u }));
    (remote.wiki || []).forEach((w) => r.push({ kind: "wiki", id: w.slug || w.id, label: w.title, sub: "Wiki article", ref: w }));
    (remote.forum || []).forEach((t) => r.push({ kind: "forum", id: t.id, label: t.title, sub: `Forum · @${t.username}`, ref: t }));
    (remote.news || []).forEach((n) => r.push({ kind: "news", id: n.id, label: n.title, sub: n.category ? `News · ${n.category}` : "News", ref: n }));
    (remote.comments || []).forEach((c) => r.push({ kind: "comment", id: c.id, label: c.snippet || "(comment)", sub: `Comment · @${c.username || ""}`, ref: c }));
    (remote.tour || []).forEach((t) => r.push({ kind: "tour", id: t.id, label: `${t.city || ""}${t.country ? ", " + t.country : ""}`, sub: `${t.venue || "Tour stop"}${t.date ? " · " + t.date : ""}`, ref: t }));
    (remote.games || []).forEach((g) => r.push({ kind: "game", id: g.id, label: g.title, sub: g.subtitle || "Game", ref: g }));
    (remote.shop || []).forEach((s) => r.push({ kind: "shop", id: s.id, label: s.title, sub: s.price ? `${s.category || "Shop"} · ${s.price}` : (s.category || "Shop"), ref: s }));
    (remote.gallery || []).forEach((g) => r.push({ kind: "gallery", id: g.id, label: g.title || "Image", sub: g.category ? `Gallery · ${g.category}` : "Gallery", ref: g }));
    (remote.wallpapers || []).forEach((w) => r.push({ kind: "wallpaper", id: w.id, label: w.title || "Wallpaper", sub: "Wallpapers", ref: w }));
    return r;
  }, [catalogResults, remote]);

  useEffect(() => { setActiveIdx(0); }, [q]);

  // === Item activation ===
  const activate = useCallback((item) => {
    if (!item) return;
    onClose();
    if (item.kind === "song" || item.kind === "album" || item.kind === "ep") {
      // Encode in URL so target page can open the modal automatically.
      navigate(`/discography?open=${item.kind}:${item.id}`);
    } else if (item.kind === "user") {
      navigate(`/u/${item.id}`);
    } else if (item.kind === "wiki") {
      navigate(`/wiki/${item.id}`);
    } else if (item.kind === "forum") {
      navigate(`/forum/topic/${item.id}`);
    } else if (item.kind === "news") {
      navigate(`/news/${item.ref.slug || item.id}`);
    } else if (item.kind === "comment") {
      const slug = item.ref.news_slug || item.ref.news_id;
      navigate(`/news/${slug}#c-${item.id}`);
    } else if (item.kind === "tour") {
      navigate(`/concert/${item.id}`);
    } else if (item.kind === "game") {
      navigate(item.ref.link || "/games");
    } else if (item.kind === "shop") {
      navigate(`/marketplace?listing=${item.id}`);
    } else if (item.kind === "gallery") {
      navigate(`/gallery?open=${item.id}`);
    } else if (item.kind === "wallpaper") {
      navigate(`/gallery`);
    }
  }, [navigate, onClose]);

  // Arrow keys on the whole modal
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); activate(flat[activeIdx]); }
  };

  const coverFor = (item) => {
    if (item.kind === "song") return resolveSongCover(item.ref, ALBUM_COVERS)?.src;
    if (item.kind === "album") return ALBUM_COVERS[item.ref.id]?.src || item.ref.cover;
    if (item.kind === "ep") return EP_COVERS[item.ref.id]?.src;
    if (item.kind === "user") return item.ref.avatar;
    if (item.kind === "wiki") return item.ref.thumbnail;
    if (item.kind === "news") return item.ref.cover;
    if (item.kind === "wallpaper") return item.ref.image;
    if (item.kind === "shop") return item.ref.image;
    if (item.kind === "gallery") return item.ref.image;
    return null;
  };
  const iconFor = (kind) => ({
    song: <Music2 size={12}/>, album: <Disc3 size={12}/>, ep: <Radio size={12}/>,
    user: <User2 size={12}/>, wiki: <BookOpen size={12}/>, forum: <MessagesSquare size={12}/>,
    news: <Newspaper size={12}/>, comment: <MessagesSquare size={12}/>,
    tour: <MapPin size={12}/>, game: <Gamepad2 size={12}/>, shop: <ShoppingBag size={12}/>,
    gallery: <Picture size={12}/>, wallpaper: <Sparkles size={12}/>,
  })[kind] || <Hash size={12}/>;

  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        onClick={onClose}
        data-testid="search-overlay"
        className="fixed inset-0 z-[120] bg-[#020B1F]/80 backdrop-blur-md grid place-items-start justify-center pt-[12vh] p-3 sm:p-6"
        onKeyDown={onKeyDown}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.97, opacity: 0, y: 8 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl glass-strong rounded-3xl border border-[#00AFFF]/30 shadow-[0_30px_80px_rgba(0,175,255,0.25)] overflow-hidden"
        >
          {/* Input row */}
          <div className="flex items-center gap-3 px-4 sm:px-5 py-3.5 border-b border-white/10">
            <Search size={18} className="text-[#00AFFF] shrink-0"/>
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search songs, albums, fans, wiki, forum…"
              data-testid="search-input"
              className="flex-1 bg-transparent outline-none text-white text-base placeholder:text-white/45 font-body"
            />
            {q && (
              <button onClick={() => setQ("")} aria-label="Clear" className="w-7 h-7 grid place-items-center rounded-full text-white/55 hover:text-white hover:bg-white/5">
                <X size={14}/>
              </button>
            )}
            <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] text-white/55 bg-white/5 border border-white/10 font-mono">ESC</kbd>
          </div>

          {/* Results */}
          <div className="max-h-[70vh] overflow-y-auto px-3 py-2">
            {q.trim().length < 2 ? (
              <EmptyState onPick={setQ}/>
            ) : flat.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-white/55 font-body">
                {busy ? <Tx>Searching…</Tx> : <><Tx>Nothing found for</Tx> &ldquo;<span className="text-white">{q}</span>&rdquo;.</>}
              </div>
            ) : (
              <Groups items={flat} activeIdx={activeIdx} onActivate={activate} coverFor={coverFor} iconFor={iconFor}/>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Groups({ items, activeIdx, onActivate, coverFor, iconFor }) {
  // Group while preserving the original flat index for keyboard nav.
  const groups = items.reduce((acc, it, idx) => {
    const key = it.kind;
    acc[key] = acc[key] || [];
    acc[key].push({ ...it, _idx: idx });
    return acc;
  }, {});
  const order = ["song", "album", "ep", "game", "news", "comment", "tour", "shop", "user", "forum", "wiki", "gallery", "wallpaper"];
  const labels = {
    song: "Songs", album: "Albums", ep: "EPs",
    user: "Fans", wiki: "Wiki", forum: "Forum",
    news: "News", comment: "News comments",
    tour: "Tour stops", game: "Games", shop: "Shop",
    gallery: "Gallery", wallpaper: "Wallpapers",
  };
  return (
    <div className="divide-y divide-white/5">
      {order.filter((k) => groups[k]?.length).map((k) => (
        <div key={k} className="py-2">
          <div className="px-3 py-1.5 text-[9px] uppercase tracking-[0.3em] text-white/45 font-bold">{labels[k]}</div>
          <ul>
            {groups[k].map((it) => (
              <li key={`${it.kind}-${it.id}`}>
                <button
                  onClick={() => onActivate(it)}
                  data-testid={`search-result-${it.kind}-${it.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition text-left ${activeIdx === it._idx ? "bg-[#00AFFF]/15 ring-1 ring-[#00AFFF]/40" : "hover:bg-white/5"}`}
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/5 border border-white/10 shrink-0 grid place-items-center text-white/50">
                    {coverFor(it) ? (
                      <img src={coverFor(it)} alt="" className="w-full h-full object-cover"/>
                    ) : (
                      iconFor(it.kind)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-display font-bold text-white truncate">{it.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-white/45 truncate">{it.sub}</div>
                  </div>
                  <span className="text-[#00AFFF]">{iconFor(it.kind)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onPick }) {
  const examples = ["Blue", "Europop", "Bestiale", "Loredana", "Move Your Body", "Sanremo", "Auto Blu"];
  return (
    <div className="px-3 py-5 space-y-3">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#00AFFF] font-bold">Try searching for</div>
      <div className="flex flex-wrap gap-2">
        {examples.map((ex) => (
          <button key={ex} onClick={() => onPick(ex)} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white hover:bg-[#00AFFF]/15 hover:border-[#00AFFF]/40 transition">
            {ex}
          </button>
        ))}
      </div>
      <div className="text-[10px] text-white/40 mt-4">
        Press <kbd className="px-1 py-0.5 rounded bg-white/5 border border-white/10 font-mono">↑↓</kbd> to navigate, <kbd className="px-1 py-0.5 rounded bg-white/5 border border-white/10 font-mono">Enter</kbd> to open.
      </div>
    </div>
  );
}
